import { HTMLProps, ReactNode, forwardRef } from 'react';

import { classNames } from '@/utils/classnames';

import type { IconProps } from '../IconProps';

type Variant = 'success' | 'error' | 'warning' | 'info';

const SUCCESS_COLOR = '#3EBE6A';
const ERROR_COLOR = '#E0646B';
const INFO_COLOR = '#4D5BFF';

const CheckMarkIcon = (props: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    width={props.width ?? 24}
    height={props.height ?? 24}
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M23.069 3.94772C23.6501 4.53811 23.6427 5.48782 23.0523 6.06898L8.83005 20.069C8.24624 20.6437 7.30932 20.6437 6.7255 20.069L0.947724 14.3815C0.357344 13.8003 0.349866 12.8506 0.931022 12.2602C1.51218 11.6698 2.4619 11.6624 3.05228 12.2435L7.77778 16.8952L20.9477 3.93102C21.5381 3.34987 22.4878 3.35734 23.069 3.94772Z"
      fill="url(#modalBadgeSuccessGradient)"
    />
    <defs>
      <linearGradient
        id="modalBadgeSuccessGradient"
        x1="12"
        y1="20.5"
        x2="12"
        y2="3.5"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor={SUCCESS_COLOR} stopOpacity="0.46" />
        <stop offset="1" stopColor={SUCCESS_COLOR} />
      </linearGradient>
    </defs>
  </svg>
);

const ErrorIcon = (props: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    width={props.width ?? 24}
    height={props.height ?? 24}
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.3335 3.125C7.43197 3.125 3.4585 7.09847 3.4585 12C3.4585 16.9015 7.43197 20.875 12.3335 20.875C17.235 20.875 21.2085 16.9015 21.2085 12C21.2085 7.09847 17.235 3.125 12.3335 3.125ZM1.2085 12C1.2085 5.85583 6.18933 0.875 12.3335 0.875C18.4777 0.875 23.4585 5.85583 23.4585 12C23.4585 18.1442 18.4777 23.125 12.3335 23.125C6.18933 23.125 1.2085 18.1442 1.2085 12ZM8.538 8.20451C8.97734 7.76517 9.68965 7.76517 10.129 8.20451L12.3335 10.409L14.538 8.20451C14.9773 7.76517 15.6897 7.76517 16.129 8.20451C16.5683 8.64385 16.5683 9.35616 16.129 9.7955L13.9245 12L16.129 14.2045C16.5683 14.6438 16.5683 15.3562 16.129 15.7955C15.6897 16.2348 14.9773 16.2348 14.538 15.7955L12.3335 13.591L10.129 15.7955C9.68965 16.2348 8.97734 16.2348 8.538 15.7955C8.09866 15.3562 8.09866 14.6438 8.538 14.2045L10.7425 12L8.538 9.7955C8.09866 9.35616 8.09866 8.64385 8.538 8.20451Z"
      fill="url(#modalBadgeErrorGradient)"
    />
    <defs>
      <linearGradient
        id="modalBadgeErrorGradient"
        x1="12.3335"
        y1="23.125"
        x2="12.3335"
        y2="0.875"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor={ERROR_COLOR} stopOpacity="0.46" />
        <stop offset="1" stopColor={ERROR_COLOR} />
      </linearGradient>
    </defs>
  </svg>
);

const WarningIcon = (props: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    width={props.width ?? 24}
    height={props.height ?? 24}
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.3335 3.125C7.43197 3.125 3.4585 7.09847 3.4585 12C3.4585 16.9015 7.43197 20.875 12.3335 20.875C17.235 20.875 21.2085 16.9015 21.2085 12C21.2085 7.09847 17.235 3.125 12.3335 3.125ZM1.2085 12C1.2085 5.85583 6.18933 0.875 12.3335 0.875C18.4777 0.875 23.4585 5.85583 23.4585 12C23.4585 18.1442 18.4777 23.125 12.3335 23.125C6.18933 23.125 1.2085 18.1442 1.2085 12ZM12.3335 6.875C12.9548 6.875 13.4585 7.37868 13.4585 8V12C13.4585 12.6213 12.9548 13.125 12.3335 13.125C11.7122 13.125 11.2085 12.6213 11.2085 12V8C11.2085 7.37868 11.7122 6.875 12.3335 6.875ZM11.2085 16C11.2085 15.3787 11.7122 14.875 12.3335 14.875H12.3435C12.9648 14.875 13.4685 15.3787 13.4685 16C13.4685 16.6213 12.9648 17.125 12.3435 17.125H12.3335C11.7122 17.125 11.2085 16.6213 11.2085 16Z"
      fill="url(#modalBadgeWarningGradient)"
    />
    <defs>
      <linearGradient
        id="modalBadgeWarningGradient"
        x1="12.3335"
        y1="23.125"
        x2="12.3335"
        y2="0.875"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor={ERROR_COLOR} stopOpacity="0.46" />
        <stop offset="1" stopColor={ERROR_COLOR} />
      </linearGradient>
    </defs>
  </svg>
);

const InfoIcon = (props: IconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    width={props.width ?? 24}
    height={props.height ?? 24}
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.3335 20.875C7.43197 20.875 3.4585 16.9015 3.4585 12C3.4585 7.09847 7.43197 3.125 12.3335 3.125C17.235 3.125 21.2085 7.09847 21.2085 12C21.2085 16.9015 17.235 20.875 12.3335 20.875ZM1.2085 12C1.2085 18.1442 6.18933 23.125 12.3335 23.125C18.4777 23.125 23.4585 18.1442 23.4585 12C23.4585 5.85583 18.4777 0.874998 12.3335 0.874998C6.18933 0.874998 1.2085 5.85583 1.2085 12ZM12.3335 17.125C12.9548 17.125 13.4585 16.6213 13.4585 16V12C13.4585 11.3787 12.9548 10.875 12.3335 10.875C11.7122 10.875 11.2085 11.3787 11.2085 12V16C11.2085 16.6213 11.7122 17.125 12.3335 17.125ZM11.2085 8C11.2085 8.62132 11.7122 9.125 12.3335 9.125H12.3435C12.9648 9.125 13.4685 8.62132 13.4685 8C13.4685 7.37868 12.9648 6.875 12.3435 6.875H12.3335C11.7122 6.875 11.2085 7.37868 11.2085 8Z"
      fill="url(#modalBadgeInfoGradient)"
    />
    <defs>
      <linearGradient
        id="modalBadgeInfoGradient"
        x1="12.3335"
        y1="0.874998"
        x2="12.3335"
        y2="23.125"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor={INFO_COLOR} />
        <stop offset="1" stopColor={INFO_COLOR} stopOpacity="0.46" />
      </linearGradient>
    </defs>
  </svg>
);

const variantMapping: Record<Variant, ReactNode> = {
  success: <CheckMarkIcon />,
  warning: <WarningIcon />,
  error: <ErrorIcon />,
  info: <InfoIcon />,
};

const CIRCLE_CLASS = [
  'w-16',
  'h-16',
  'rounded-full',
  'flex',
  'justify-center',
  'items-center',
  '[background:var(--gradient-token-modal-badge-bg)]',
  '[box-shadow:var(--shadow-modal-badge)]',
].join(' ');

export const ModalBadge = forwardRef<
  HTMLDivElement,
  { variant: Variant } & Pick<HTMLProps<HTMLDivElement>, 'style' | 'className'>
>(function ModalBadge(props, ref) {
  const { variant, className, style } = props;
  return (
    <div ref={ref} className={classNames(CIRCLE_CLASS, className)} style={style}>
      {variantMapping[variant]}
    </div>
  );
});
